import { Link } from 'react-router-dom';
import { ShieldAlert, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/contexts/SharedAuthContext';
import { useFinopsAlertsLatest, useFinopsAlertsSummary } from '@/hooks/useFinops';
import { EmptyState } from './shared';
import { SeverityDot } from './AlertSeverityBadge';
import { DeadlineText, ServiceChip } from './AlertRow';
import { ALERTS_PATH, FINOPS_ALERTS_SUBTAB, alertHref, fmtDateUTC } from './alerts-format';

/**
 * Overview panel — the latest open alerts. The deny check lives HERE and is not
 * redundant with the route guard: this renders on /technology/finops, which only
 * the tab:finops gate protects. Checking before the body mounts also means a
 * denied user fires no alerts query.
 */
export function AlertsPanel() {
  const { user } = useAuth();
  const denied: string[] = user?.deniedPermissions ?? [];
  if (denied.includes(FINOPS_ALERTS_SUBTAB)) return null;
  return <AlertsPanelBody />;
}

function AlertsPanelBody() {
  const { t } = useTranslation();
  const latest = useFinopsAlertsLatest(5);
  const summary = useFinopsAlertsSummary();

  const header = (
    <div className="flex items-center justify-between gap-2">
      <h3 className="flex items-center gap-2 text-sm font-semibold">
        <ShieldAlert className="h-4 w-4 text-rose-600" />
        {t('finops.alerts.panel.title')}
        {summary.data && summary.data.openCritical + summary.data.openWarning > 0 && (
          <span className="rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
            {summary.data.openCritical + summary.data.openWarning}
          </span>
        )}
      </h3>
      <Link to={ALERTS_PATH} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        {t('finops.alerts.panel.view_all')}
        <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );

  if (latest.isLoading || summary.isLoading) {
    return (
      <Card className="p-4">
        {header}
        <p className="mt-2 h-5 w-48 animate-pulse rounded bg-muted" />
      </Card>
    );
  }

  if (latest.isError || summary.isError) {
    return (
      <Card className="p-4">
        {header}
        <p className="mt-2 text-xs text-muted-foreground">{t('finops.alerts.panel.error')}</p>
      </Card>
    );
  }

  const feedSince = summary.data?.healthFeedSince ?? null;
  if (!feedSince) {
    return (
      <EmptyState
        title={t('finops.alerts.empty.feed_inactive.title')}
        description={t('finops.alerts.empty.feed_inactive.description')}
      />
    );
  }

  const items = latest.data ?? [];
  return (
    <Card className="p-4">
      {header}
      {items.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {t('finops.alerts.panel.no_open', { date: fmtDateUTC(feedSince) })}
        </p>
      ) : (
        <ul className="mt-3 divide-y">
          {items.map((a) => (
            <li key={a.id}>
              <Link
                to={alertHref(a.id)}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded px-1 py-2 text-sm hover:bg-muted/40"
              >
                <SeverityDot severity={a.severity} />
                <ServiceChip service={a.service} />
                <span className="font-medium min-w-0 truncate">{a.title}</span>
                <span className="text-muted-foreground">{a.accountName}</span>
                <span className="ml-auto flex items-center gap-3 text-xs">
                  {a.deadline && <DeadlineText deadline={a.deadline} />}
                  {(a.pendingCount ?? 0) > 0 && (
                    <span className="text-muted-foreground">
                      {t('finops.alerts.panel.pending', { count: a.pendingCount })}
                    </span>
                  )}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
